import { useState, useMemo, useEffect } from 'react'
import maturityAssessmentData from '../../../../shared/data/maturityAssessment.json'
import { useAIJobs } from '../../../../contexts/AIJobsContext'

// Build a lookup of question id → { question, category }
const QUESTION_LOOKUP = (() => {
  const map = {}
  for (const section of maturityAssessmentData.sections || []) {
    for (const q of section.questions || []) {
      map[q.id] = { question: q.question, category: section.name }
    }
  }
  return map
})()

function FollowUpsView({ client }) {
  const { startJob, getJob } = useAIJobs()
  const assessments = useMemo(() => client.assessments || [], [client.assessments])

  // Default to in-progress, fall back to most recent
  const defaultAssessmentId = useMemo(() => {
    const inProgress = assessments.find(a => a.status === 'in_progress')
    if (inProgress) return inProgress.id
    const sorted = [...assessments].sort((a, b) =>
      new Date(b.updatedAt || b.createdAt) - new Date(a.updatedAt || a.createdAt)
    )
    return sorted[0]?.id || null
  }, [assessments])

  const [selectedAssessmentId, setSelectedAssessmentId] = useState(defaultAssessmentId)
  // Selection state stays local — checkbox UI is per-mount and not worth persisting.
  // Generated cluster results live in AIJobsContext, keyed by assessment id, so they
  // survive navigation away from this view.
  const [selectedByAssessment, setSelectedByAssessment] = useState({}) // { [id]: Set<questionId> }
  const [error, setError] = useState(null)
  const [emailDraft, setEmailDraft] = useState(null) // { subject, body, sourceAssessmentId }

  const jobKey = selectedAssessmentId ? `followup-consolidate:${selectedAssessmentId}` : null
  const job = getJob(jobKey)
  const loading = job?.status === 'running'
  const currentResult = job?.status === 'success' ? job.result : null

  // Surface job errors
  useEffect(() => {
    if (job?.status === 'error') setError(job.error)
  }, [job?.status, job?.error])

  // When a fresh result lands, pre-select all items by default (only if not already set)
  useEffect(() => {
    if (currentResult && selectedAssessmentId && !selectedByAssessment[selectedAssessmentId]) {
      const allIds = new Set()
      ;(currentResult.clusters || []).forEach(c => (c.items || []).forEach(i => allIds.add(i.questionId)))
      setSelectedByAssessment(prev => ({ ...prev, [selectedAssessmentId]: allIds }))
    }
  }, [currentResult, selectedAssessmentId, selectedByAssessment])

  useEffect(() => {
    if (defaultAssessmentId && !selectedAssessmentId) {
      setSelectedAssessmentId(defaultAssessmentId)
    }
  }, [defaultAssessmentId, selectedAssessmentId])

  const selectedAssessment = useMemo(
    () => assessments.find(a => a.id === selectedAssessmentId),
    [assessments, selectedAssessmentId]
  )

  // Hydrate flagged questions for the selected assessment
  const flaggedItems = useMemo(() => {
    if (!selectedAssessment) return []
    const flags = selectedAssessment.responses?.flags
      || selectedAssessment.responses?.notes?.flags
      || {}
    const maturity = selectedAssessment.responses?.maturityAssessment || {}
    return Object.entries(flags).map(([questionId, flagData]) => {
      const lookup = QUESTION_LOOKUP[questionId] || {}
      const answer = maturity[questionId] || {}
      return {
        questionId,
        question: lookup.question || questionId,
        category: lookup.category || 'Other',
        currentAnswer: answer.value || 'unanswered',
        answerNote: answer.notes || '',
        advisorNote: flagData?.reason || '',
      }
    })
  }, [selectedAssessment])

  const currentSelected = selectedByAssessment[selectedAssessmentId] || new Set()

  const generateConsolidation = () => {
    if (!jobKey || flaggedItems.length === 0) return
    setError(null)
    // Reset selection so the next successful result re-selects all items
    setSelectedByAssessment(prev => {
      if (!(selectedAssessmentId in prev)) return prev
      const next = { ...prev }
      delete next[selectedAssessmentId]
      return next
    })
    startJob(jobKey, async () => {
      const res = await fetch('/api/ai/followup-consolidate', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          assessmentId: selectedAssessmentId,
          clientName: client.name,
          industry: client.industry,
          items: flaggedItems,
        }),
      })
      if (!res.ok) {
        const errBody = await res.json().catch(() => ({}))
        throw new Error(errBody.error || `Request failed (${res.status})`)
      }
      const data = await res.json()
      return {
        clusters: data.clusters || [],
        generatedAt: new Date().toISOString(),
      }
    }).catch(err => {
      console.error('[FollowUpsView] Consolidation failed:', err)
    })
  }

  const toggleSelection = (questionId) => {
    setSelectedByAssessment(prev => {
      const current = new Set(prev[selectedAssessmentId] || [])
      if (current.has(questionId)) current.delete(questionId)
      else current.add(questionId)
      return { ...prev, [selectedAssessmentId]: current }
    })
  }

  const composeEmail = () => {
    if (!currentResult) return
    const selectedItems = []
    currentResult.clusters.forEach(cluster => {
      const items = (cluster.items || []).filter(i => currentSelected.has(i.questionId))
      if (items.length > 0) {
        selectedItems.push({ theme: cluster.theme, items })
      }
    })

    if (selectedItems.length === 0) {
      setError('Select at least one follow-up item to compose an email')
      return
    }

    const greeting = `Hi ${client.contact_name?.split(' ')[0] || 'there'},`
    const intro = `Thank you again for your time on the discovery assessment for ${client.name}. As we work to finalize the maturity scoring, a few items came up where we'd value a bit more detail. Could you help us close these out?`

    let body = `${greeting}\n\n${intro}\n\n`
    selectedItems.forEach(group => {
      body += `${group.theme}\n`
      group.items.forEach((item, idx) => {
        body += `  ${idx + 1}. ${item.ask}\n`
      })
      body += `\n`
    })
    body += `Happy to jump on a quick call if it's easier to walk through these together — just let me know what works.\n\nThanks,\n`

    setEmailDraft({
      subject: `${client.name} — Follow-up questions from discovery assessment`,
      body,
      sourceAssessmentId: selectedAssessmentId,
    })
    setError(null)
  }

  const copyEmail = async () => {
    if (!emailDraft) return
    const text = `Subject: ${emailDraft.subject}\n\n${emailDraft.body}`
    try {
      await navigator.clipboard.writeText(text)
    } catch (err) {
      console.error('Copy failed:', err)
    }
  }

  if (assessments.length === 0) {
    return (
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-12 text-center">
        <svg className="w-12 h-12 text-gray-400 dark:text-gray-500 mx-auto mb-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" />
        </svg>
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No assessments yet</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400">Create an assessment to start tracking follow-ups.</p>
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* Header / assessment selector */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Follow-up Questions</h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
              Consolidated from flagged questions in the selected assessment
            </p>
          </div>
          {assessments.length > 1 && (
            <select
              value={selectedAssessmentId || ''}
              onChange={(e) => setSelectedAssessmentId(e.target.value)}
              className="text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md px-3 py-1.5"
            >
              {assessments.map(a => (
                <option key={a.id} value={a.id}>
                  {a.title || `Assessment ${new Date(a.createdAt).toLocaleDateString()}`}
                  {a.status === 'in_progress' ? ' (in progress)' : ' (completed)'}
                </option>
              ))}
            </select>
          )}
        </div>
      </div>

      {/* Flagged items summary */}
      <div className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 p-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-9 h-9 rounded-full bg-orange-100 dark:bg-orange-900/30">
              <svg className="w-4 h-4 text-orange-600 dark:text-orange-400" fill="currentColor" viewBox="0 0 24 24">
                <path d="M3 21v-4m0 0V5a2 2 0 012-2h6.5l1 1H21l-3 6 3 6h-8.5l-1-1H5a2 2 0 00-2 2z" />
              </svg>
            </span>
            <div>
              <div className="text-sm font-semibold text-gray-900 dark:text-white">
                {flaggedItems.length} flagged question{flaggedItems.length === 1 ? '' : 's'}
              </div>
              <div className="text-xs text-gray-500 dark:text-gray-400">
                {currentResult
                  ? `Last consolidated ${new Date(currentResult.generatedAt).toLocaleString()}`
                  : 'Not yet consolidated'}
              </div>
            </div>
          </div>
          <button
            onClick={generateConsolidation}
            disabled={loading || flaggedItems.length === 0}
            className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <svg className="w-4 h-4 mr-1.5 animate-spin" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Consolidating…
              </>
            ) : currentResult ? (
              <>
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Regenerate
              </>
            ) : (
              <>
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
                </svg>
                Consolidate with AI
              </>
            )}
          </button>
        </div>

        {flaggedItems.length === 0 && (
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-3 pt-3 border-t border-gray-100 dark:border-gray-700">
            No flagged questions in this assessment yet. Flag a question and add a follow-up note while taking the assessment to populate this list.
          </p>
        )}
      </div>

      {error && (
        <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3 text-sm text-red-700 dark:text-red-300">
          {error}
        </div>
      )}

      {/* Curated clusters */}
      {currentResult && currentResult.clusters.length > 0 && (
        <>
          <div className="flex items-center justify-between">
            <div className="text-xs text-gray-500 dark:text-gray-400">
              {currentSelected.size} of {currentResult.clusters.reduce((sum, c) => sum + (c.items?.length || 0), 0)} selected
            </div>
            <button
              onClick={composeEmail}
              disabled={currentSelected.size === 0}
              className="btn-primary text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
              </svg>
              Compose Email ({currentSelected.size})
            </button>
          </div>

          {currentResult.clusters.map((cluster, ci) => (
            <div key={ci} className="bg-white dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700 overflow-hidden">
              <div className="px-4 py-3 border-b border-gray-100 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50">
                <h4 className="text-sm font-semibold text-gray-900 dark:text-white">{cluster.theme}</h4>
                {cluster.summary && (
                  <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{cluster.summary}</p>
                )}
              </div>
              <div className="divide-y divide-gray-100 dark:divide-gray-700">
                {(cluster.items || []).map(item => {
                  const isSelected = currentSelected.has(item.questionId)
                  return (
                    <label
                      key={item.questionId}
                      className="flex items-start gap-3 px-4 py-3 cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700/30 transition-colors"
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSelection(item.questionId)}
                        className="mt-1 w-4 h-4 rounded border-gray-300 text-primary-600 focus:ring-primary-500"
                      />
                      <div className="flex-1 min-w-0">
                        <p className="text-sm text-gray-900 dark:text-white">{item.ask}</p>
                        <p className="text-xs text-gray-400 dark:text-gray-500 mt-1">
                          {item.category || cluster.theme} · {item.questionId}
                        </p>
                      </div>
                    </label>
                  )
                })}
              </div>
            </div>
          ))}
        </>
      )}

      {/* Email draft modal */}
      {emailDraft && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={() => setEmailDraft(null)} />
          <div className="relative bg-white dark:bg-gray-800 rounded-xl shadow-2xl border border-gray-200 dark:border-gray-700 w-full max-w-2xl max-h-[85vh] flex flex-col">
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 dark:border-gray-700">
              <h3 className="text-base font-semibold text-gray-900 dark:text-white">Email Draft</h3>
              <button
                onClick={() => setEmailDraft(null)}
                className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                </svg>
              </button>
            </div>
            <div className="flex-1 overflow-y-auto p-5 space-y-4">
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Subject</label>
                <input
                  type="text"
                  value={emailDraft.subject}
                  onChange={(e) => setEmailDraft({ ...emailDraft, subject: e.target.value })}
                  className="w-full text-sm border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md px-3 py-2"
                />
              </div>
              <div>
                <label className="block text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Body</label>
                <textarea
                  value={emailDraft.body}
                  onChange={(e) => setEmailDraft({ ...emailDraft, body: e.target.value })}
                  rows={18}
                  className="w-full text-sm font-mono border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md px-3 py-2 resize-y"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 px-5 py-4 border-t border-gray-200 dark:border-gray-700">
              <button
                onClick={() => setEmailDraft(null)}
                className="px-4 py-2 text-sm font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600"
              >
                Close
              </button>
              <button
                onClick={copyEmail}
                className="btn-primary text-sm"
              >
                <svg className="w-4 h-4 mr-1.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                </svg>
                Copy to Clipboard
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default FollowUpsView
